import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Bot, MessageSquare, Send, Activity, Brain, Loader2, Plus, UserCircle, Info } from 'lucide-react';
import { useCommunitiesStore } from '@/stores/agents/communities-store';
import { useChatStore, ChatMode } from '@/stores/agents/chat-store';
import { cn } from '@/lib/utils';
import { eventsApi, memoriesApi, userAgentApi, normalizeForSpeech, AgentEvent, AgentMemory } from '@/services/api/agentApi';
import { VoiceMicButton } from '@/components/ai/VoiceMicButton';
import { useVoiceSession } from '@/hooks/useVoiceSession';
import { PipecatClientProvider, PipecatClientAudio } from '@pipecat-ai/client-react';
import { UserAgentProfile } from './UserAgentProfile';
import { ChatSourcesPanel } from '@/components/ai/ChatSourcesPanel';

function isAgentNamingQuestion(question: string): boolean {
  return /what would you like to call me|pick any name/i.test(question);
}

export const MyAgentsTab: React.FC = () => {
  const { userCommunity, fetchUserCommunity } = useCommunitiesStore();
  const {
    threads, activeThreadId, messages, isLoadingMessages, isSending, chatStatus, chatMode,
    fetchThreads, loadThread, startNewChat, sendMessage, fetchStatus, setChatMode,
    initUserAgentSocket,
  } = useChatStore();

  const {
    voiceState, error: voiceError, isConnected: voiceConnected,
    client: voiceClient,
    connect: voiceConnect, disconnect: voiceDisconnect,
    setOnTranscription, setOnBotResponse,
  } = useVoiceSession();

  const [recentEvents, setRecentEvents] = useState<AgentEvent[]>([]);
  const [recentMemories, setRecentMemories] = useState<AgentMemory[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [showProfile, setShowProfile] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const pendingAutoSendRef = useRef(false);
  const [onboardingAnswers, setOnboardingAnswers] = useState<Record<string, string>>({});
  const [isSubmittingOnboarding, setIsSubmittingOnboarding] = useState(false);
  const [expandedSources, setExpandedSources] = useState<Record<string, boolean>>({});

  useEffect(() => {
    fetchUserCommunity();
    fetchStatus();
    fetchThreads();
    initUserAgentSocket();
  }, []);

  useEffect(() => {
    if (userCommunity) {
      eventsApi.list({ community_id: userCommunity.community_id, limit: '10' })
        .then(r => setRecentEvents(r.data || []))
        .catch(() => {});
      memoriesApi.list({ community_id: userCommunity.community_id, limit: '10' })
        .then(r => setRecentMemories(r.data || []))
        .catch(() => {});
    }
  }, [userCommunity]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Wire voice transcription to auto-send (fires on EndOfTurn from voice service)
  useEffect(() => {
    setOnTranscription((text: string) => {
      setInputValue(text);
      pendingAutoSendRef.current = true;
    });
  }, [setOnTranscription]);

  // Auto-send when voice transcription arrives
  useEffect(() => {
    if (pendingAutoSendRef.current && inputValue.trim()) {
      pendingAutoSendRef.current = false;
      handleSend();
    }
  }, [inputValue]);

  // When agent responds via text chat, send the response to voice service for TTS
  const prevMessageCountRef = useRef(messages.length);
  const lastSpokenRef = useRef<string>('');
  useEffect(() => {
    if (!voiceClient || !voiceConnected) return;

    const lastMsg = messages[messages.length - 1];
    if (!lastMsg || lastMsg.role !== 'assistant') return;
    if (!lastMsg.content || lastMsg.content === 'Thinking...') return;
    // Don't speak the same message twice
    if (lastMsg.content === lastSpokenRef.current) return;
    // Only speak completed messages (status === 'done' or no status)
    if ((lastMsg as any).status && (lastMsg as any).status !== 'done') return;

    lastSpokenRef.current = lastMsg.content;  // Must stay as display text (dedup guard)
    const displayText = lastMsg.content;
    console.log('[MyAgentsTab] Normalizing for TTS:', displayText.slice(0, 80));
    // IIFE: useEffect callbacks must be synchronous; async/await lives inside
    (async () => {
      const speechText = await normalizeForSpeech(displayText);
      console.log('[MyAgentsTab] Sending to TTS (normalized):', speechText.slice(0, 80));
      voiceClient.sendText(speechText, { run_immediately: true, audio_response: true }).catch((err: Error) => {
        console.warn('[MyAgentsTab] Failed to send text to voice:', err);
      });
    })();
  }, [messages, voiceClient, voiceConnected]);

  // Voice mic toggle handler
  const handleMicPress = useCallback(async () => {
    if (voiceConnected) {
      voiceDisconnect();
    } else {
      await voiceConnect();
    }
  }, [voiceConnected, voiceConnect, voiceDisconnect]);

  // User agent mode always available; community mode requires orchestrators
  const chatEnabled = chatMode === 'user_agent' ? true : (chatStatus?.available ?? false);

  const refreshActivity = () => {
    if (userCommunity) {
      eventsApi.list({ community_id: userCommunity.community_id, limit: '10' })
        .then(r => setRecentEvents(r.data || []))
        .catch(() => {});
      memoriesApi.list({ community_id: userCommunity.community_id, limit: '10' })
        .then(r => setRecentMemories(r.data || []))
        .catch(() => {});
    }
  };

  const handleSend = async () => {
    const text = inputValue.trim();
    if (!text || (isSending && chatMode !== 'user_agent') || !chatEnabled) return;

    setInputValue('');
    await sendMessage(text, userCommunity?.community_id);
    refreshActivity();
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleOnboardingSubmit = async (messageId: string, question: string) => {
    const answer = (onboardingAnswers[messageId] ?? '').trim();
    if (!answer || isSubmittingOnboarding) return;
    setIsSubmittingOnboarding(true);
    try {
      const res = await userAgentApi.submitOnboardingAnswer(question, answer);
      setOnboardingAnswers((prev) => {
        const next = { ...prev };
        delete next[messageId];
        return next;
      });
      useChatStore.setState((s) => {
        const messages = [...s.messages];
        const resolvedMessageIndex = messages.findIndex((msg) => msg.id === messageId);
        const nextAgentName = isAgentNamingQuestion(question) && answer.toLowerCase() !== 'skip'
          ? answer
          : s.agentName;

        if (resolvedMessageIndex !== -1) {
          const resolvedMessage = messages[resolvedMessageIndex];
          if (resolvedMessage.content.trim()) {
            messages[resolvedMessageIndex] = {
              ...resolvedMessage,
              onboardingQuestion: null,
            };
          } else {
            messages.splice(resolvedMessageIndex, 1);
          }
        }

        messages.push({
          id: `onboarding-answer-${Date.now()}`,
          role: 'user',
          content: answer,
          timestamp: new Date(),
          status: 'done',
        });

        if (res.data.nextQuestion) {
          messages.push({
            id: `onboarding-${Date.now()}`,
            role: 'assistant',
            content: '',
            timestamp: new Date(),
            onboardingQuestion: res.data.nextQuestion,
          });
        } else if (res.data.onboardingComplete) {
          messages.push({
            id: `onboarding-done-${Date.now()}`,
            role: 'assistant',
            content: 'Onboarding complete! I now have a better understanding of how to work with you.',
            timestamp: new Date(),
          });
        }

        return {
          messages,
          agentName: nextAgentName,
        };
      });
    } catch {
      // User can retry
    } finally {
      setIsSubmittingOnboarding(false);
    }
  };

  const timeAgo = (date: string) => {
    const diff = Date.now() - new Date(date).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  return (
    <div className="h-full flex flex-col lg:flex-row overflow-hidden">
      {/* Left: Thread sidebar */}
      <div className="w-56 flex-shrink-0 border-r border-border flex flex-col bg-muted/20">
        <div className="p-3 border-b border-border space-y-2">
          <button
            onClick={() => { startNewChat(); setShowProfile(false); }}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            <Plus className="h-4 w-4" />
            New Chat
          </button>
          <button
            onClick={() => setShowProfile(!showProfile)}
            className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              showProfile ? 'bg-accent text-foreground' : 'hover:bg-muted text-muted-foreground'
            }`}
          >
            <UserCircle className="h-4 w-4" />
            My Agent Profile
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {threads.length === 0 ? (
            <p className="text-xs text-muted-foreground p-3">
              {chatMode === 'user_agent' ? 'Personal agent chat — no threads' : 'No conversations yet'}
            </p>
          ) : (
            threads.map((thread) => (
              <button
                key={thread.thread_id}
                onClick={() => { loadThread(thread.thread_id); setShowProfile(false); }}
                className={`w-full text-left px-3 py-2.5 border-b border-border/50 hover:bg-muted/60 transition-colors ${
                  activeThreadId === thread.thread_id ? 'bg-accent' : ''
                }`}
              >
                <p className="text-sm truncate">{thread.title || 'Untitled'}</p>
                <p className="text-[10px] text-muted-foreground mt-0.5">
                  {timeAgo(thread.updated_at)}
                  {thread.turn_count > 1 && ` · ${thread.turn_count} turns`}
                </p>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Center: Chat area or Profile */}
      {showProfile ? (
        <div className="flex-1 overflow-y-auto p-6 border-r border-border">
          <UserAgentProfile />
        </div>
      ) : (
      <div className="flex-1 flex flex-col border-r border-border min-w-0">
        {/* Stats header with mode toggle */}
        <div className="flex items-center gap-4 px-6 py-3 border-b border-border bg-muted/30">
          <div className="flex items-center gap-1 bg-muted rounded-lg p-0.5">
            <button
              onClick={() => setChatMode('community')}
              className={cn(
                'px-3 py-1 rounded-md text-xs font-medium transition-colors',
                chatMode === 'community' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'
              )}
            >
              Community
            </button>
            <button
              onClick={() => setChatMode('user_agent')}
              className={cn(
                'px-3 py-1 rounded-md text-xs font-medium transition-colors',
                chatMode === 'user_agent' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'
              )}
            >
              My Agent
            </button>
          </div>
          {chatMode === 'community' && userCommunity && (
            <>
              <span className="text-xs text-muted-foreground">
                Events: <span className="font-medium">{recentEvents.length}</span>
              </span>
              <span className="text-xs text-muted-foreground">
                Memories: <span className="font-medium">{recentMemories.length}</span>
              </span>
            </>
          )}
          {chatStatus && chatMode === 'community' && (
            <span className="text-xs text-muted-foreground ml-auto">
              <span className={`inline-block w-2 h-2 rounded-full mr-1 ${chatStatus.available ? 'bg-emerald-500' : 'bg-red-400'}`} />
              {chatStatus.available ? `${chatStatus.orchestratorCount} orchestrator${chatStatus.orchestratorCount !== 1 ? 's' : ''} online` : 'Offline'}
            </span>
          )}
          {chatMode === 'user_agent' && (
            <span className="text-xs text-muted-foreground ml-auto">
              <span className="inline-block w-2 h-2 rounded-full mr-1 bg-emerald-500" />
              Personal agent
            </span>
          )}
        </div>

        {/* Chat messages area */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {isLoadingMessages ? (
            <div className="h-full flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : messages.length === 0 ? (
            <div className="h-full flex items-center justify-center">
              <div className="text-center space-y-4">
                <div className="relative">
                  <Bot className="h-20 w-20 mx-auto text-muted-foreground/30" />
                  <MessageSquare className="h-8 w-8 absolute bottom-0 right-1/2 translate-x-8 text-primary/40" />
                </div>
                <h3 className="text-lg font-medium text-muted-foreground">
                  {chatMode === 'user_agent' ? 'Your Personal Agent' : 'Your AI Agent Community'}
                </h3>
                <p className="text-sm text-muted-foreground/70 max-w-md">
                  {chatMode === 'user_agent'
                    ? 'Chat directly with your personal agent. Ask about your preferences, expertise profile, or anything personal.'
                    : chatEnabled
                      ? 'Send a message to interact with your AI agents. Ask questions, request analysis, or get insights from your data.'
                      : 'Chat will be enabled when orchestrators are running.'}
                </p>
              </div>
            </div>
          ) : (
            messages.map((msg) => {
              // Phase 6: training-only responses get a distinct, neutral style
              // so the user can see at a glance that the answer isn't grounded
              // in their data. Subtle — not alarming.
              const isTrainingOnly = msg.role === 'assistant' && msg.trustMetadata?.trainingDataUsed === true;
              return (
              <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] rounded-lg px-4 py-3 ${
                  msg.role === 'user'
                    ? 'bg-primary text-primary-foreground'
                    : isTrainingOnly
                      ? 'bg-gray-100 text-gray-700 border border-dashed border-gray-300'
                      : 'bg-muted'
                }`}>
                  {msg.status === 'thinking' ? (
                    <div className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">{msg.content}</span>
                    </div>
                  ) : msg.content ? (
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  ) : null}
                  {msg.onboardingQuestion && (
                    <div className="mt-2 p-3 rounded-md bg-primary/5 border border-primary/20">
                      <p className="text-sm font-medium mb-2">{msg.onboardingQuestion}</p>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          className="flex-1 px-3 py-1.5 border border-border rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                          placeholder="Your answer..."
                          value={onboardingAnswers[msg.id] ?? ''}
                          onChange={(e) => setOnboardingAnswers((prev) => ({ ...prev, [msg.id]: e.target.value }))}
                          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleOnboardingSubmit(msg.id, msg.onboardingQuestion!); }}}
                          disabled={isSubmittingOnboarding}
                        />
                        <button
                          className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                            (onboardingAnswers[msg.id] ?? '').trim() && !isSubmittingOnboarding
                              ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                              : 'bg-primary/20 text-primary cursor-not-allowed'
                          }`}
                          onClick={() => handleOnboardingSubmit(msg.id, msg.onboardingQuestion!)}
                          disabled={!(onboardingAnswers[msg.id] ?? '').trim() || isSubmittingOnboarding}
                        >
                          {isSubmittingOnboarding ? 'Sending...' : 'Answer'}
                        </button>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-[10px] opacity-60">
                      {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                    {msg.usage && (
                      <span className="text-[10px] opacity-60">
                        {msg.usage.modelName} | {msg.usage.tokensInput + msg.usage.tokensOutput} tokens
                      </span>
                    )}
                    {msg.role === 'assistant' && (msg.sources !== undefined || msg.trustMetadata?.trainingDataUsed) && (
                      <button
                        type="button"
                        onClick={() =>
                          setExpandedSources((prev) => ({ ...prev, [msg.id]: !prev[msg.id] }))
                        }
                        className="inline-flex items-center gap-1 text-[10px] opacity-70 hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-400 rounded"
                        aria-expanded={!!expandedSources[msg.id]}
                        aria-label={
                          expandedSources[msg.id]
                            ? 'Hide response sources'
                            : `Show response sources (${msg.sources?.length ?? 0})`
                        }
                      >
                        <Info className="w-3 h-3" aria-hidden="true" />
                        <span>
                          {msg.sources && msg.sources.length > 0
                            ? `Sources (${msg.sources.length})`
                            : 'Sources'}
                        </span>
                      </button>
                    )}
                  </div>
                  {msg.role === 'assistant' && expandedSources[msg.id] && (
                    <ChatSourcesPanel
                      sources={msg.sources ?? []}
                      trustMetadata={msg.trustMetadata}
                    />
                  )}
                </div>
              </div>
              );
            })
          )}
          {isSending && chatMode === 'community' && (
            <div className="flex justify-start">
              <div className="bg-muted rounded-lg px-4 py-3 flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Thinking...</span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Chat input */}
        <div className="flex-shrink-0 border-t border-border p-4">
          <div className="flex items-center gap-2">
            <input
              ref={inputRef}
              type="text"
              placeholder={chatMode === 'user_agent' ? 'Talk to your personal agent...' : chatEnabled ? 'Ask your agents anything...' : 'Waiting for orchestrators...'}
              className="flex-1 px-4 py-2 border border-border rounded-lg bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={!chatEnabled || (isSending && chatMode !== 'user_agent')}
            />
            <VoiceMicButton
              voiceState={voiceState}
              onPress={handleMicPress}
              disabled={isSending && chatMode !== 'user_agent'}
              error={voiceError}
            />
            <button
              className={`p-2 rounded-lg transition-colors ${
                chatEnabled && inputValue.trim() && (!isSending || chatMode === 'user_agent')
                  ? 'bg-primary text-primary-foreground hover:bg-primary/90 cursor-pointer'
                  : 'bg-primary/20 text-primary cursor-not-allowed'
              }`}
              onClick={handleSend}
              disabled={!chatEnabled || !inputValue.trim() || (isSending && chatMode !== 'user_agent')}
            >
              {isSending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
            </button>
          </div>
          {!chatEnabled && (
            <p className="text-xs text-muted-foreground mt-1">Chat will be enabled when orchestrators are running</p>
          )}
        </div>
      </div>
      )}

      {/* Right: Activity panel */}
      <div className="w-full lg:w-80 flex-shrink-0 overflow-y-auto">
        {/* Recent Events */}
        <div className="p-4 border-b border-border">
          <div className="flex items-center gap-2 mb-3">
            <Activity className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-sm font-medium">My Recent Events</h3>
          </div>
          {recentEvents.length === 0 ? (
            <p className="text-xs text-muted-foreground">No events yet</p>
          ) : (
            <div className="space-y-2">
              {recentEvents.slice(0, 10).map((event) => (
                <div key={event._id} className="text-xs p-2 bg-muted/50 rounded">
                  <p className="font-medium truncate">{event.description}</p>
                  <div className="flex items-center justify-between mt-1 text-muted-foreground">
                    <span className="bg-secondary px-1.5 py-0.5 rounded">{event.category}</span>
                    <span>{timeAgo(event.started_at)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Memories */}
        <div className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Brain className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-sm font-medium">My Memories</h3>
          </div>
          {recentMemories.length === 0 ? (
            <p className="text-xs text-muted-foreground">No memories yet</p>
          ) : (
            <div className="space-y-2">
              {recentMemories.slice(0, 10).map((memory) => (
                <div key={memory._id} className="text-xs p-2 bg-muted/50 rounded">
                  <p className="truncate">{memory.text}</p>
                  <div className="flex items-center justify-between mt-1">
                    <span className={`px-1.5 py-0.5 rounded ${
                      memory.type === 'hypothesis' ? 'bg-amber-500/10 text-amber-600' : 'bg-emerald-500/10 text-emerald-600'
                    }`}>
                      {memory.type}
                    </span>
                    <div className="flex items-center gap-1">
                      <div className="w-12 h-1 bg-secondary rounded-full overflow-hidden">
                        <div className="h-full bg-primary rounded-full" style={{ width: `${memory.weight * 100}%` }} />
                      </div>
                      <span className="text-muted-foreground">{(memory.weight * 100).toFixed(0)}%</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Pipecat audio playback — renders a hidden <audio> element for bot TTS */}
      {voiceClient && (
        <PipecatClientProvider client={voiceClient}>
          <PipecatClientAudio />
        </PipecatClientProvider>
      )}
    </div>
  );
};
